import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  Users,
  Mail,
  CheckCircle,
  XCircle,
  Link2,
  AlertTriangle
} from 'lucide-react';

const STAFF_TYPES = [
  { value: 'classroom_teacher', label: 'Classroom Teacher', chipLabel: 'Classroom Teacher' },
  { value: 'school_administrator', label: 'School Administrator', chipLabel: 'Admin' },
  { value: 'speech_language_pathologist', label: 'Speech-Language Pathologist', chipLabel: 'SLP' },
  { value: 'counselor', label: 'Counselor', chipLabel: 'Counselor' },
  { value: 'instructional_coach', label: 'Instructional Coach', chipLabel: 'Coach' },
  { value: 'reading_specialist', label: 'Reading Specialist', chipLabel: 'Reading Specialist' },
  { value: 'math_specialist', label: 'Math Specialist', chipLabel: 'Math Specialist' },
  { value: 'behavior_specialist', label: 'Behavior Specialist', chipLabel: 'Behavior Specialist' },
  { value: 'intervention_support_staff', label: 'Intervention Support Staff', chipLabel: 'Intervention Support' },
  { value: 'school_psychologist', label: 'School Psychologist', chipLabel: 'Psychologist' },
  { value: 'other', label: 'Other', chipLabel: 'Other' }
];

// User-facing role labels (backend 'specialist' = UI 'Interventionist')
const ROLE_DISPLAY_LABELS = {
  super_admin: 'Super Admin',
  admin: 'School Admin',
  specialist: 'Interventionist',
  viewer: 'Viewer'
};

const getRoleDisplayLabel = (role) => {
  return ROLE_DISPLAY_LABELS[role] || role;
};

const getStaffTypeLabel = (type, otherValue, useChipLabel = false) => {
  // If type is "other" and we have a custom value, show it
  if (type === 'other' && otherValue) {
    return otherValue;
  }
  const found = STAFF_TYPES.find(t => t.value === type);
  if (found) {
    return useChipLabel ? found.chipLabel : found.label;
  }
  return type;
};

// Get short chip label for table display
const getStaffTypeChipLabel = (type, otherValue) => {
  return getStaffTypeLabel(type, otherValue, true);
};

export default function StaffDirectoryPage() {
  const { selectedSchoolId, isSuperAdmin, user, userSchoolId } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [schools, setSchools] = useState([]);
  const [selectedSchoolFilter, setSelectedSchoolFilter] = useState(selectedSchoolId || 'all');
  
  // Determine the effective school ID for non-super-admins (School Admins use their assigned school)
  const effectiveSchoolIdForForm = isSuperAdmin ? null : userSchoolId;

  // Fetch schools for super admin
  useEffect(() => {
    if (isSuperAdmin) {
      apiService.getSchools().then(res => setSchools(res.data)).catch(console.error);
    }
  }, [isSuperAdmin]);

  // Fetch contacts
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = {
        active_only: !showInactive,
        staff_type: filterType !== 'all' ? filterType : undefined,
        school_id: isSuperAdmin && selectedSchoolFilter !== 'all' ? selectedSchoolFilter : selectedSchoolId
      };
      const res = await apiService.getStaffContacts(params);
      setContacts(res.data);
    } catch (error) {
      console.error('Failed to fetch staff contacts:', error);
      toast.error('Failed to load staff directory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [selectedSchoolId, filterType, showInactive, selectedSchoolFilter]);

  // Filter by search term
  const filteredContacts = contacts.filter(c => {
    const searchLower = searchTerm.toLowerCase();
    return (
      c.display_name?.toLowerCase().includes(searchLower) ||
      c.email?.toLowerCase().includes(searchLower) ||
      c.first_name?.toLowerCase().includes(searchLower) ||
      c.last_name?.toLowerCase().includes(searchLower)
    );
  });

  const handleAddContact = () => {
    setEditingContact(null);
    setShowModal(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setShowModal(true);
  };

  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Are you sure you want to ${contact.active ? 'deactivate' : 'delete'} ${contact.display_name}?`)) return;
    
    try {
      await apiService.deleteStaffContact(contact.id);
      toast.success(contact.active ? 'Staff contact deactivated' : 'Staff contact deleted');
      fetchContacts();
    } catch (error) {
      console.error('Failed to delete staff contact:', error);
      toast.error('Failed to delete staff contact');
    }
  };

  // Migration functionality removed - use backend scripts for production safety
  // Script location: /app/scripts/migrate_staff_contacts.py

  const handleModalClose = () => {
    setShowModal(false);
    setEditingContact(null);
  };

  const handleModalSave = () => {
    setShowModal(false);
    setEditingContact(null);
    fetchContacts();
  };

  // Count contacts needing email
  const contactsNeedingEmail = contacts.filter(c => c.needs_email || (!c.email && c.migrated_from_legacy)).length;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title" data-testid="staff-directory-title">Staff Directory</h1>
          <p className="page-subtitle">Manage school staff and classroom teachers</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Migration button removed - use backend scripts for production safety */}
          <Button 
            className="bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl"
            onClick={handleAddContact}
            data-testid="add-staff-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Staff Contact
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {contactsNeedingEmail > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              {contactsNeedingEmail} staff contact{contactsNeedingEmail > 1 ? 's' : ''} missing email address
            </p>
            <p className="text-xs text-amber-600 mt-0.5">
              Email addresses are needed for future messaging and reliable matching during CSV imports.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-6">
        <div className="p-4 flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#E5E5E5] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B]"
              data-testid="staff-search-input"
            />
          </div>

          {/* School filter (Super Admin) */}
          {isSuperAdmin && schools.length > 0 && (
            <select
              value={selectedSchoolFilter}
              onChange={(e) => setSelectedSchoolFilter(e.target.value)}
              className="px-3 py-2 border border-[#E5E5E5] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
              data-testid="school-filter-select"
            >
              <option value="all">All Schools</option>
              {schools.map(school => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
          )}

          {/* Staff type filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-[#E5E5E5] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
            data-testid="type-filter-select"
          >
            <option value="all">All Types</option>
            {STAFF_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>

          {/* Show inactive toggle */}
          <label className="flex items-center gap-2 text-sm text-[#595959] cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-[#E5E5E5]"
            />
            Show inactive
          </label>
        </div>
      </div>

      {/* Staff List */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <Users className="h-5 w-5 inline mr-2 text-[#2C4C3B]" />
            Staff Contacts ({filteredContacts.length})
          </h3>
        </div>
        <div className="card-content p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B]" />
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="text-center py-12 text-[#A3A3A3]">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-base">No staff contacts found</p>
              <p className="text-sm mt-1">Add staff contacts to assign teachers, link login users, and identify intervention providers.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="staff-table">
                <thead>
                  <tr className="border-b border-[#E5E5E5]">
                    <th className="text-left py-3 px-4 text-xs font-medium text-[#595959] uppercase tracking-wider">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-[#595959] uppercase tracking-wider">Email</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-[#595959] uppercase tracking-wider">Type</th>
                    {isSuperAdmin && <th className="text-left py-3 px-4 text-xs font-medium text-[#595959] uppercase tracking-wider">School</th>}
                    <th className="text-left py-3 px-4 text-xs font-medium text-[#595959] uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-[#595959] uppercase tracking-wider">Linked User</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-[#595959] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map(contact => (
                    <tr 
                      key={contact.id} 
                      className="border-b border-[#E5E5E5] hover:bg-[#F3F4F1]/50 transition-colors"
                      data-testid={`staff-row-${contact.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="font-medium text-[#1C1C1C]">{contact.display_name}</div>
                        {contact.first_name && contact.last_name && contact.display_name !== `${contact.first_name} ${contact.last_name}` && (
                          <div className="text-xs text-[#A3A3A3]">{contact.first_name} {contact.last_name}</div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {contact.email ? (
                          <div className="flex items-center gap-1.5 text-sm text-[#595959]">
                            <Mail className="h-3.5 w-3.5" />
                            {contact.email}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <AlertTriangle className="h-3 w-3" />
                            Needs Email
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#2C4C3B]/10 text-[#2C4C3B]">
                            {getStaffTypeChipLabel(contact.staff_type, contact.staff_type_other)}
                          </span>
                          {contact.can_provide_interventions && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              Provider
                            </span>
                          )}
                        </div>
                      </td>
                      {isSuperAdmin && (
                        <td className="py-3 px-4 text-sm text-[#595959]">
                          {contact.school_name || '-'}
                        </td>
                      )}
                      <td className="py-3 px-4">
                        {contact.active ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle className="h-3.5 w-3.5" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-[#A3A3A3]">
                            <XCircle className="h-3.5 w-3.5" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {contact.linked_user_id ? (
                          <span className="inline-flex items-center gap-1 text-xs text-[#2C4C3B]">
                            <Link2 className="h-3.5 w-3.5" />
                            {contact.linked_user_email || 'Linked User'}
                          </span>
                        ) : contact.can_provide_interventions ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <AlertTriangle className="h-3 w-3" />
                            No Login
                          </span>
                        ) : (
                          <span className="text-xs text-[#A3A3A3]">No Login</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditContact(contact)}
                            className="h-8 w-8 p-0 text-[#595959] hover:text-[#2C4C3B]"
                            data-testid={`edit-staff-${contact.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteContact(contact)}
                            className="h-8 w-8 p-0 text-[#595959] hover:text-red-600"
                            data-testid={`delete-staff-${contact.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <StaffContactModal
          contact={editingContact}
          schoolId={isSuperAdmin ? (selectedSchoolFilter !== 'all' ? selectedSchoolFilter : '') : effectiveSchoolIdForForm}
          schools={isSuperAdmin ? schools : []}
          isSuperAdmin={isSuperAdmin}
          userSchoolId={userSchoolId}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
}

// Staff Contact Modal Component - Email-First Workflow
function StaffContactModal({ contact, schoolId, schools, isSuperAdmin, userSchoolId, onClose, onSave }) {
  // For non-super-admins, ensure we have a valid school assignment
  const hasSchoolAssignment = isSuperAdmin || !!userSchoolId;
  
  const [formData, setFormData] = useState({
    school_id: contact?.school_id || schoolId || '',
    email: contact?.email || '',
    first_name: contact?.first_name || '',
    last_name: contact?.last_name || '',
    display_name: contact?.display_name || '',
    staff_type: contact?.staff_type || 'classroom_teacher',
    staff_type_other: contact?.staff_type_other || '',
    linked_user_id: contact?.linked_user_id || '',
    can_provide_interventions: contact?.can_provide_interventions ?? false,
    active: contact?.active ?? true
  });
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [nameInferred, setNameInferred] = useState(false);

  // Fetch users for linking (specialists/admins in the same school)
  // NOTE: All hooks must be called before any early returns (React rules of hooks)
  useEffect(() => {
    if (hasSchoolAssignment && formData.school_id) {
      apiService.getUsers().then(res => {
        const schoolUsers = res.data.filter(u => 
          u.school_id === formData.school_id || u.role === 'super_admin'
        );
        setUsers(schoolUsers);
      }).catch(console.error);
    }
  }, [formData.school_id, hasSchoolAssignment]);

  // Auto-infer name from email when email changes (for new contacts)
  useEffect(() => {
    if (!hasSchoolAssignment) return; // Skip if no school assignment
    if (!contact && formData.email && formData.email.includes('@')) {
      // Infer name from email local part
      const localPart = formData.email.split('@')[0].toLowerCase();
      let parts = [];
      for (const sep of ['.', '_', '-']) {
        if (localPart.includes(sep)) {
          parts = localPart.split(sep);
          break;
        }
      }
      if (!parts.length) parts = [localPart];
      
      const capitalizedParts = parts.map(p => p.charAt(0).toUpperCase() + p.slice(1));
      
      let inferredFirst = '';
      let inferredLast = '';
      let inferredDisplay = '';
      
      if (capitalizedParts.length >= 2) {
        inferredFirst = capitalizedParts[0];
        inferredLast = capitalizedParts.slice(1).join(' ');
        inferredDisplay = `${inferredFirst} ${inferredLast}`;
      } else if (capitalizedParts.length === 1) {
        inferredLast = capitalizedParts[0];
        inferredDisplay = capitalizedParts[0];
      }
      
      // Only auto-fill if fields are empty and we haven't already inferred
      if (!nameInferred && !formData.first_name && !formData.last_name && !formData.display_name) {
        setFormData(prev => ({
          ...prev,
          first_name: inferredFirst,
          last_name: inferredLast,
          display_name: inferredDisplay
        }));
        setNameInferred(true);
      }
    }
  }, [formData.email, contact, nameInferred, formData.first_name, formData.last_name, formData.display_name, hasSchoolAssignment]);

  // Reset inferred flag when email changes significantly
  useEffect(() => {
    if (!formData.email || !formData.email.includes('@')) {
      setNameInferred(false);
    }
  }, [formData.email]);
  
  // If School Admin has no school assignment, show error state
  // NOTE: This early return must come AFTER all hooks are called
  if (!isSuperAdmin && !hasSchoolAssignment) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
          <div className="p-6 border-b border-[#E5E5E5]">
            <h2 className="text-lg font-semibold text-[#1C1C1C]">Unable to Add Staff Contact</h2>
          </div>
          <div className="p-6">
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Your account is not assigned to a school.</p>
                <p className="text-sm text-red-700 mt-1">Please contact a Super Admin to assign your account to a school before adding staff contacts.</p>
              </div>
            </div>
          </div>
          <div className="p-6 border-t border-[#E5E5E5] flex justify-end">
            <Button type="button" variant="outline" onClick={onClose} data-testid="staff-modal-close-btn">
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Email is required for new contacts
    if (!contact && !formData.email) {
      toast.error('Email is required');
      return;
    }
    
    if (formData.email && !formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!formData.school_id) {
      toast.error('Please select a school');
      return;
    }

    // Require custom staff type when "Other" is selected
    if (formData.staff_type === 'other' && !formData.staff_type_other?.trim()) {
      toast.error('Please specify the staff type');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        email: formData.email?.toLowerCase().trim() || null,
        display_name: formData.display_name || formData.email?.split('@')[0] || 'Unknown',
        linked_user_id: formData.linked_user_id || null,
        staff_type_other: formData.staff_type === 'other' ? formData.staff_type_other?.trim() : null
      };

      if (contact) {
        await apiService.updateStaffContact(contact.id, data);
        toast.success('Staff contact updated');
      } else {
        await apiService.createStaffContact(data);
        toast.success('Staff contact created');
      }
      onSave();
    } catch (error) {
      console.error('Failed to save staff contact:', error);
      toast.error(error.response?.data?.detail || 'Failed to save staff contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E5E5E5]">
          <h2 className="text-lg font-semibold text-[#1C1C1C]">
            {contact ? 'Edit Staff Contact' : 'Add Staff Contact'}
          </h2>
          <p className="text-sm text-[#595959] mt-1">
            {contact ? 'Update staff contact information' : 'Create a new staff contact record'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* School (Super Admin only) */}
          {isSuperAdmin && schools.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">School *</label>
              <select
                value={formData.school_id}
                onChange={(e) => setFormData({ ...formData, school_id: e.target.value })}
                className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
                required
                data-testid="staff-school-select"
              >
                <option value="">Select a school</option>
                {schools.map(school => (
                  <option key={school.id} value={school.id}>{school.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Email - Primary identifier */}
          <div>
            <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">
              Email {!contact && '*'}
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                setNameInferred(false); // Allow re-inference on email change
              }}
              placeholder="teacher@school.edu"
              className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
              required={!contact}
              data-testid="staff-email-input"
            />
            <p className="text-xs text-[#A3A3A3] mt-1">
              Teacher email is required so student assignments, progress updates, and future messaging stay connected correctly.
            </p>
          </div>

          {/* Name suggestion notice */}
          {!contact && formData.email && formData.email.includes('@') && (formData.first_name || formData.last_name) && (
            <div className="bg-[#F3F4F1] border border-[#E5E5E5] rounded-lg p-3">
              <p className="text-xs text-[#595959]">
                <strong>Name suggested from email.</strong> Name will be suggested from email and can be edited.
              </p>
            </div>
          )}

          {/* Name Fields - Auto-suggested from email, editable */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">First Name</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                placeholder="Auto-suggested from email"
                className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
                data-testid="staff-first-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">Last Name</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                placeholder="Auto-suggested from email"
                className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
                data-testid="staff-last-name-input"
              />
            </div>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">Display Name</label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              placeholder="Auto-generated from name or email"
              className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
              data-testid="staff-display-name-input"
            />
            <p className="text-xs text-[#A3A3A3] mt-1">How this person's name appears to users (e.g., "Sarah Thompson")</p>
          </div>

          {/* Staff Type */}
          <div>
            <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">Staff Type *</label>
            <select
              value={formData.staff_type}
              onChange={(e) => {
                const newType = e.target.value;
                setFormData({ 
                  ...formData, 
                  staff_type: newType,
                  // Clear custom field if not "other"
                  staff_type_other: newType !== 'other' ? '' : formData.staff_type_other
                });
              }}
              className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
              required
              data-testid="staff-type-select"
            >
              {STAFF_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Custom Staff Type when "Other" is selected */}
          {formData.staff_type === 'other' && (
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">Specify Staff Type *</label>
              <input
                type="text"
                value={formData.staff_type_other || ''}
                onChange={(e) => setFormData({ ...formData, staff_type_other: e.target.value })}
                placeholder="e.g., Occupational Therapist"
                className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
                required
                data-testid="staff-type-other-input"
              />
            </div>
          )}

          {/* Link to User (Optional) */}
          <div>
            <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">Link to App User (Optional)</label>
            <select
              value={formData.linked_user_id}
              onChange={(e) => setFormData({ ...formData, linked_user_id: e.target.value })}
              className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
              data-testid="staff-linked-user-select"
            >
              <option value="">Not linked to a user account</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.name} ({user.email}) - {getRoleDisplayLabel(user.role)}</option>
              ))}
            </select>
            <p className="text-xs text-[#A3A3A3] mt-1">If this staff member also has app login access</p>
          </div>

          {/* Can Provide Interventions - Prominent Toggle */}
          <div className={`border-2 rounded-lg p-4 ${formData.can_provide_interventions ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-white'}`}>
            <label className="flex items-center justify-between gap-4 cursor-pointer">
              <div>
                <span className="text-sm font-semibold text-[#1C1C1C]">Can provide interventions</span>
                <p className="text-xs text-[#595959] mt-0.5">
                  Turn this on if this person may be assigned as the provider for a student intervention.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs font-medium ${!formData.can_provide_interventions ? 'text-[#595959]' : 'text-[#A3A3A3]'}`}>No</span>
                <input
                  type="checkbox"
                  checked={formData.can_provide_interventions}
                  onChange={(e) => setFormData({ ...formData, can_provide_interventions: e.target.checked })}
                  className="w-9 h-5 rounded-full appearance-none bg-gray-300 checked:bg-green-600 cursor-pointer transition-colors relative before:content-[''] before:absolute before:w-4 before:h-4 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 before:transition-transform checked:before:translate-x-4"
                  data-testid="staff-can-provide-interventions"
                />
                <span className={`text-xs font-medium ${formData.can_provide_interventions ? 'text-green-700' : 'text-[#A3A3A3]'}`}>Yes</span>
              </div>
            </label>
          </div>

          {/* Active Status */}
          <div>
            <label className="flex items-center gap-2 text-sm text-[#1C1C1C] cursor-pointer">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="rounded border-[#E5E5E5]"
              />
              Active
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E5E5]">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl"
              data-testid="save-staff-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                contact ? 'Update Contact' : 'Create Contact'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
